import React, { createContext, useContext, useEffect, useState } from 'react';
import { SystemState, SystemConfiguration } from './SystemState';
import { useApi } from './ApiContext';

interface SystemContextType {
  systemState: SystemState;
  isLoading: boolean;
  error: string | null;
  configuration: SystemConfiguration | null;
  domains: string[];
  primaryDomain: string;
  rootUrl: string;
  smsEnabled: boolean;
  mfaRequired: boolean;
  generateAgentEmail: (teamName: string, agentName: string) => string;
}

// Create the context with default values
const SystemContext = createContext<SystemContextType>({
  systemState: SystemState.getInstance(),
  isLoading: true,
  error: null,
  configuration: null,
  domains: [],
  primaryDomain: '',
  rootUrl: '',
  smsEnabled: false,
  mfaRequired: false,
  generateAgentEmail: () => '',
});

export const SystemProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { config } = useApi();
  const systemState = SystemState.getInstance();
  const [state, setState] = useState<SystemContextType>({
    systemState,
    isLoading: systemState.isLoading,
    error: systemState.error,
    configuration: systemState.configuration,
    domains: systemState.domains,
    primaryDomain: systemState.primaryDomain,
    rootUrl: systemState.rootUrl,
    smsEnabled: systemState.smsEnabled,
    mfaRequired: systemState.mfaRequired,
    generateAgentEmail: systemState.generateAgentEmail.bind(systemState),
  });

  useEffect(() => {
    // Initialize the system state
    systemState.initialize(config);

    // Add observer to update state when system state changes
    const removeObserver = systemState.addObserver((updatedState) => {
      setState({
        systemState: updatedState,
        isLoading: updatedState.isLoading,
        error: updatedState.error,
        configuration: updatedState.configuration,
        domains: updatedState.domains,
        primaryDomain: updatedState.primaryDomain,
        rootUrl: updatedState.rootUrl,
        smsEnabled: updatedState.smsEnabled,
        mfaRequired: updatedState.mfaRequired,
        generateAgentEmail: updatedState.generateAgentEmail.bind(updatedState),
      });
    });

    // Clean up observer on unmount
    return () => {
      removeObserver();
    };
  }, [config]);

  return (
    <SystemContext.Provider value={state}>{children}</SystemContext.Provider>
  );
};

export const useSystemContext = () => useContext(SystemContext);
